"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, type FormEvent } from "react";
import { ApiError, errorMessage, post } from "@/lib/api";
import { homePathFor, useAuth } from "@/lib/auth";
import type { LoginResponse } from "@/lib/types";
import { AuthShell } from "@/components/AuthShell";
import { Guard } from "@/components/Guard";
import { KakaoButton } from "@/components/KakaoButton";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Field";
import { Alert, Divider } from "@/components/ui/Surface";

export default function LoginPage() {
  return (
    <Guard need="anonymous">
      <LoginForm />
    </Guard>
  );
}

function LoginForm() {
  const { signIn } = useAuth();
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setLoading(true);
    try {
      const res = await post<LoginResponse>("/v1/auth/login", { email, password });
      signIn(res);
      router.replace(homePathFor(res.user));
    } catch (err) {
      if (err instanceof ApiError && err.code === "VALIDATION_FAILED") setFieldErrors(err.fieldErrors);
      else setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="로그인"
      description="입찰각 계정으로 로그인하세요."
      footer={
        <>
          아직 계정이 없으신가요?{" "}
          <Link href="/signup" className="font-semibold text-primary-900">
            회원가입
          </Link>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <KakaoButton />
        <Divider label="또는 이메일로 로그인" />
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <TextField
            label="이메일"
            type="email"
            autoComplete="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldErrors.email}
          />
          <TextField
            label="비밀번호"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors.password}
          />
          {error && <Alert tone="negative">{error}</Alert>}
          <Button type="submit" size="lg" block loading={loading} className="mt-2">
            로그인
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
